import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DatePicker } from 'primeng/datepicker';
import { SelectModule } from 'primeng/select';
import { BlockUI } from 'primeng/blockui';
import { ProgressSpinner } from 'primeng/progressspinner';
import { Button } from 'primeng/button';
import { ResultsApi } from '@features/results/services/results-api';
import { BusinessStateService } from '@core/services/business-state.service';
import { Subject, takeUntil, skip, forkJoin } from 'rxjs';
import { MonthlyReportData } from '@features/reports/models/results.model';
import { ResultsByMonthList } from '@features/reports/components/results-by-month-list/results-by-month-list';

interface DropdownOption {
  label: string;
  value: string;
}

@Component({
  selector: 'app-results-by-month',
  imports: [FormsModule, DatePicker, SelectModule, BlockUI, ProgressSpinner, Button, ResultsByMonthList],
  templateUrl: './results-by-month.html'
})
export class ResultsByMonth implements OnInit, OnDestroy {
  private readonly resultsApi = inject(ResultsApi);
  private readonly businessStateService = inject(BusinessStateService);
  private readonly destroy$ = new Subject<void>();

  reportDataScope: MonthlyReportData | null = null;
  reportDataCategory: MonthlyReportData | null = null;
  isLoading = false;

  pcgOptions: DropdownOption[] = [
    { label: 'AR4', value: 'AR4' },
    { label: 'AR5', value: 'AR5' },
    { label: 'AR6', value: 'AR6' },
  ];

  selectedYear: Date = new Date();
  selectedPCG: string = 'AR6';

  ngOnInit(): void {
    this.fetchReportResults();

    this.businessStateService.selectedBusiness$
      .pipe(
        skip(1),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.fetchReportResults();
      });
  }

  fetchReportResults() {
    if (this.selectedYear && this.selectedPCG) {
      const year = this.selectedYear.getFullYear();
      const pcg = this.selectedPCG;
      const selectedBusiness = this.businessStateService.getSelectedBusiness();
      const selectedId = selectedBusiness?.data?.id;

      if (!selectedId) {
        console.error('No hay negocio o sede seleccionada');
        return;
      }

      this.isLoading = true;
      this.reportDataScope = null;
      this.reportDataCategory = null;

      const requestScope = { officeId: selectedId, year, gwp: pcg, grouped: 'scope' };
      const requestCategory = { officeId: selectedId, year, gwp: pcg, grouped: 'category' };

      forkJoin({
        scope: this.resultsApi.getMonthlyResults(requestScope),
        category: this.resultsApi.getMonthlyResults(requestCategory)
      }).subscribe({
        next: (response: any) => {
          this.reportDataScope = response.scope?.Data || null;
          this.reportDataCategory = response.category?.Data || null;
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error fetching report results:', error);
          this.reportDataScope = null;
          this.reportDataCategory = null;
          this.isLoading = false;
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
